import React, { useState, useEffect } from 'react';
import ReactGA from 'react-ga4';
import PropTypes from 'prop-types';
import Offcanvas from 'react-bootstrap/Offcanvas';

import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { IntlProvider } from 'react-intl';
import { Amplify } from 'aws-amplify';
import awsconfig from './aws-exports';
import { useDispatch, useSelector, batch } from 'react-redux';
import styled from 'styled-components';

import { ThemeProvider } from './hoc/ThemeProvider';

import Book from './screens/Book';
import Home from './screens/Home';
import SignIn from './screens/SignIn';
import SignUp from './screens/SignUp';
import SignUpSuccessful from './screens/SignUp/Success';

import NewBook from './screens/NewBook';
import EditBook from './screens/EditBook';
import Search from './screens/Search';
import Product from './screens/Product';
import Profile from './screens/Profile';

import Navbar from './containers/Navbar';

import { theme } from './styles/theme';

import LeftNavbar from './containers/LeftNavbar';
import ForgotPassword from './screens/ForgotPassword';
import ResetPassword from './screens/ResetPassword';
import Box from './components/layout/Box';
import { setAsked, setValue } from './state/cookieSettings/actions';
import Button from './components/Button';
import Text from './components/Text';
import CookieSettingsModal from './screens/Modals/CookieSettings';
import useAuthListener from './features/auth/hooks/useAuthListener';

Amplify.configure(awsconfig);

const StyledBox = styled(Box)`
  flex-direction: column;
  align-items: center;
  flex-grow: 1;

  @media (min-width: 769px) {
    flex-direction: row;
    align-items: flex-start;
  }
`;

const ProtectedRoute = ({ isAuth, redirectPath }) => {
  if (!isAuth) {
    return <Navigate to={redirectPath} replace />;
  }

  return <Outlet />;
};

ProtectedRoute.propTypes = {
  isAuth: PropTypes.bool.isRequired,
  redirectPath: PropTypes.string,
};

ProtectedRoute.defaultProps = {
  redirectPath: '/sign-in',
};

function App() {
  const { authLoading } = useAuthListener();

  const [show, setShow] = useState(false);
  const [showCookieSettings, setShowCookieSettings] = useState(false);
  const dispatch = useDispatch();
  const cognitoUser = useSelector((state) => state.auth.cognitoUser);

  const { value: cookieType, asked } = useSelector(
    (state) => state.cookieSettings,
  );

  useEffect(() => {
    if (cookieType === 'all') {
      ReactGA.initialize('G-44XFZH5NVQ');
    }
  }, [cookieType]);

  useEffect(() => {
    if (!asked) {
      setShow(true);
    }
  }, [asked]);

  const acceptAll = () => {
    batch(() => {
      dispatch(setValue('all'));
      dispatch(setAsked(true));
    });

    setShow(false);
  };

  const openCookieSettingsModal = () => {
    setShow(false);
    setShowCookieSettings(true);
  };

  if (authLoading) {
    return null;
  }

  return (
    <IntlProvider locale="sv" defaultLocale="sv">
      <ThemeProvider theme={theme}>
        <LeftNavbar isLoggedIn={cognitoUser} />
        <Navbar isLoggedIn={cognitoUser} />

        <Routes>
          <Route path="/book" element={<Book />} />
          <Route path="/" element={<Home />} />
          <Route path="/sign-in" element={<SignIn />} />
          <Route path="/sign-up" element={<SignUp />} />
          <Route path="/search" element={<Search />} />
          <Route path="/products" element={<Product />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          <Route path="sign-up-successful" element={<SignUpSuccessful />} />

          <Route element={<ProtectedRoute isAuth={!!cognitoUser} />}>
            <Route path="/profile" element={<Profile />} />
            <Route path="/new" element={<NewBook />} />
            <Route path="/edit" element={<EditBook />} />
          </Route>
        </Routes>

        <CookieSettingsModal
          show={showCookieSettings}
          setShow={setShowCookieSettings}
        />

        <Offcanvas placement="bottom" className="h-auto" show={show}>
          <Box
            className="flex-column"
            style={{ maxWidth: 970, alignSelf: 'center' }}
          >
            <Offcanvas.Body>
              <Box className="flex-column">
                <Text
                  style={{
                    fontWeight: '600',
                    fontSize: 18,
                  }}
                  className="mb-2"
                >
                  Vi värderar din integritet
                </Text>
                <Text style={{ fontSize: '0.85rem' }}>
                  Vi använder kakor för att förbättra din upplevelse på vår
                  webbplats. Vissa av dessa kakor är nödvändiga för att
                  webbplatsen ska fungera korrekt, medan andra hjälper oss att
                  förstå hur du använder webbplatsen så att vi kan erbjuda
                  bättre innehåll och annonsering.
                </Text>
                <StyledBox className="align-items-center mt-3">
                  <Box>
                    <Button expand onClick={acceptAll}>
                      Godkänn alla kakor
                    </Button>
                  </Box>

                  <Box
                    className="px-3 py-3"
                    onClick={openCookieSettingsModal}
                    style={{
                      cursor: 'pointer',
                    }}
                  >
                    <Text>Inställningar</Text>
                  </Box>
                </StyledBox>
              </Box>
            </Offcanvas.Body>
          </Box>
        </Offcanvas>
      </ThemeProvider>
    </IntlProvider>
  );
}

export default App;
